/**
 * Universal sharing service.
 *
 * Calls the universal sharing endpoints:
 *   GET  /api/v2/sharing/{entityType}/{entityId}
 *   POST /api/v2/sharing/{entityType}/{entityId}
 *   POST /api/v2/sharing/bulk
 */

import fpFetch from "@/services/fpFetch";
import { BASE_API_URL_V2 } from "@/constants";

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

export type EntityType =
  | "workspace"
  | "view"
  // An RMS view group (#13896): its grantees see every view in it.
  | "view-group"
  | "interaction"
  | "conversation"
  | "agent"
  | "skill"
  | "artifact"
  // An artifact folder (#14320): its grantees reach what is filed in it.
  | "artifact-folder"
  | "component";

export interface SharedUser {
  id: string;
  name: string;
  type: "view" | "edit";
  email?: string;
  /**
   * False for a Finpilot staff collaborator with no access open in the
   * organization right now: it cannot be emailed, so the Email step leaves
   * it out. Absent means emailable.
   */
  emailable?: boolean;
}

export interface SharedTeam {
  id: string;
  name: string;
  type: "view" | "edit";
}

export interface SharedOwner {
  id: string;
  name: string;
  email?: string;
  /** As on `SharedUser`. */
  emailable?: boolean;
}

export interface SharingStatusResponse {
  users: SharedUser[];
  teams: SharedTeam[];
  available_users: SharedUser[];
  available_teams: SharedTeam[];
  org_id: string | null;
  org_permission: string | null;
  // "admin" for a view's admin or a view group's owner (#13896).
  current_user_permission: "view" | "edit" | "admin";
  is_private: boolean;
  has_blob: boolean;
  link_sharing: boolean;
  entity_type: string;
  updated_at: string | null;
  owner?: SharedOwner | null;
  /**
   * True when the current user may CHANGE this entity's sharing (e.g. the
   * interaction author or a superadmin), as opposed to merely having edit
   * access to the content. The modal gates its editable controls on this.
   * When absent, the modal falls back to its owner-based heuristic. #9954
   */
  can_manage_sharing?: boolean | null;
  can_manage_organization_sharing?: boolean | null;
  /**
   * Access that reaches this entity through another one — an agent-run
   * artifact through the agent that produced it (#11941). Shown read-only
   * and never sent back as a grant; `is_private` already accounts for it.
   */
  inherited?: InheritedAccess | null;
  /**
   * An artifact's audience through the folder it is filed in (#14320), apart
   * from `inherited` (the producing agent's). Read-only here, never sent back
   * as a grant; `is_private` already accounts for it.
   */
  inherited_folder?: InheritedAccess | null;
  /**
   * A view's access through the view groups that hold it (#13896), one entry
   * per group: the group's grantees, and its owner, who edits every view in
   * it. Read-only here; it changes on the group, or by taking the view out of
   * it. Filled only for a caller who can edit the view.
   */
  inherited_groups?: InheritedGroupAccess[];
  /** The caller may take this view out of the groups in `inherited_groups`
   *  (#13896): the view's admin. */
  can_remove_from_groups?: boolean;
}

export interface InheritedGroupAccess {
  group_id: number;
  group_name: string;
  owner?: { id: string; name: string } | null;
  users: SharedUser[];
  teams: SharedTeam[];
  org_id: string | null;
  org_permission: string | null;
}

export interface InheritedAccess {
  source_type: "agent" | "folder";
  source_id: string;
  source_name: string;
  org_permission: string | null;
  users: SharedUser[];
  teams: SharedTeam[];
}

export interface ShareUserInput {
  id: string;
  type: "view" | "edit";
}

export interface ShareTeamInput {
  id: string;
  type: "view" | "edit";
}

export interface OrgShareInput {
  type: "view" | "edit";
}

export interface SharePayload {
  users: ShareUserInput[];
  teams: ShareTeamInput[];
  organization?: OrgShareInput | null;
  link_sharing?: boolean;
  updated_at?: string;
}

/**
 * A widget a dashboard's share did not fully share (#12952). "organization": its people and
 * teams were shared, but making it visible to the organization needs its creator or an admin;
 * the sharer can see it, so it has a name. "not_shareable": the sharer cannot see it, so
 * nothing was shared and its name is not theirs to read (#13714).
 */
export type NotOrganizationWideWidget = {
  component_id: string;
  name: string;
  reason: "organization";
};
export type UnsharedWidget =
  | NotOrganizationWideWidget
  | { component_id: string; name: string | null; reason: "not_shareable" };

export interface ShareResponse {
  success: boolean;
  message: string;
  /** A dashboard's share only: what became of its widgets (#12952). */
  widgets?: { shared: number; unshared: UnsharedWidget[] };
}

export interface BulkSharePayload {
  entity_type: "interaction";
  entity_ids: string[];
  users: ShareUserInput[];
  teams: ShareTeamInput[];
  organization?: OrgShareInput | null;
}

export interface BulkShareError {
  entity_id: string;
  error: string;
}

export interface BulkShareResponse {
  succeeded: string[];
  failed: BulkShareError[];
}

// ---------------------------------------------------------------------------
// API functions
// ---------------------------------------------------------------------------

export async function getSharingStatus(
  entityType: EntityType,
  entityId: string,
  includeAvailable: boolean = false
): Promise<SharingStatusResponse> {
  const params = includeAvailable ? "?include_available=true" : "";
  const url = `${BASE_API_URL_V2}/api/v2/sharing/${entityType}/${entityId}${params}`;

  const response = await fpFetch(url, {
    method: "GET",
    headers: { "Content-Type": "application/json" },
  });

  if (!response.ok) {
    const errorData = await response.json().catch(() => ({}));
    throw new Error(
      errorData.detail ||
        errorData.message ||
        `Failed to fetch sharing status: ${response.status}`
    );
  }

  return response.json();
}

export async function updateSharing(
  entityType: EntityType,
  entityId: string,
  payload: SharePayload
): Promise<ShareResponse> {
  const url = `${BASE_API_URL_V2}/api/v2/sharing/${entityType}/${entityId}`;

  const response = await fpFetch(url, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(payload),
  });

  if (!response.ok) {
    const errorData = await response.json().catch(() => ({}));
    throw new Error(
      errorData.detail ||
        errorData.message ||
        `Failed to update sharing: ${response.status}`
    );
  }

  return response.json();
}

export async function bulkShare(
  payload: BulkSharePayload
): Promise<BulkShareResponse> {
  const url = `${BASE_API_URL_V2}/api/v2/sharing/bulk`;

  const response = await fpFetch(url, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(payload),
  });

  if (!response.ok) {
    const errorData = await response.json().catch(() => ({}));
    throw new Error(
      errorData.detail ||
        errorData.message ||
        `Failed to bulk share: ${response.status}`
    );
  }

  return response.json();
}

// ---------------------------------------------------------------------------
// Emailing a shared item to people who already have access (#10815, then
// every entity type once the action reached all share modals)
// ---------------------------------------------------------------------------

export interface NotifySharePayload {
  recipient_user_ids: string[];
  message?: string;
}

/** A recipient the backend left out of a send. */
export interface NotifySkipped {
  id: string;
  name: string;
}

export interface NotifyShareResponse {
  success: boolean;
  sent_count: number;
  /** User ids whose mail could not be sent. */
  failed: string[];
  /**
   * Recipients not emailed at all: Finpilot staff without access open in
   * the organization right now. The rest of the send goes ahead.
   */
  skipped?: NotifySkipped[];
}

/**
 * Email a shared item to selected people who already have access.
 *
 * The backend builds the link and re-checks every recipient's access, so a
 * recipient the UI failed to filter is rejected rather than mailed.
 */
export async function notifyShare(
  entityType: EntityType,
  entityId: string,
  payload: NotifySharePayload
): Promise<NotifyShareResponse> {
  const url = `${BASE_API_URL_V2}/api/v2/sharing/${entityType}/${entityId}/notify`;

  const response = await fpFetch(url, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(payload),
  });

  if (!response.ok) {
    const errorData = await response.json().catch(() => ({}));
    throw new Error(
      errorData.detail ||
        errorData.message ||
        `Failed to send email: ${response.status}`
    );
  }

  return response.json();
}
